import React, { useState, useEffect } from "react";
import {
  TextField,
  Button,
  MenuItem,
  Box,
  useMediaQuery,
  useTheme,
} from "@mui/material";
import { AddressData, initialAddressData } from "../../types/Address";
import { CountyData } from "../../types/County";
import { CountryData } from "../../types/Country";
import { UserData } from "../../types/User";

interface ComponentProps {
  onSubmit: (user: Partial<AddressData>) => void;
  onClose: () => void;
  detailData: UserData;
  initialData?: Partial<AddressData>;
  countyData: CountyData[];
  countryData: CountryData[];
}

const AddressForm: React.FC<ComponentProps> = ({
  onSubmit,
  onClose,
  detailData,
  initialData,
  countyData,
  countryData,
}) => {
  const [inputData, setInputData] = useState<Partial<AddressData>>(
    initialData || initialAddressData
  );
  const [hasDefaultCountry, setHasDefaultCountry] = useState(Boolean);
  const [errors, setErrors] = useState<{ [key: string]: string }>({});

  // 🌐 detect screen size
  const theme = useTheme();
  const isMobile = useMediaQuery(theme.breakpoints.down("sm"));

  const handleChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    const { name, value, type, checked } = e.target;

    setInputData((prev: Partial<AddressData>) => ({
      ...prev,
      userId: detailData.id,
      [name]: type === "checkbox" ? checked : value,
    }));
    if (name == "countryId" && value == "LR") {
      setInputData((prev) => ({
        ...prev,
        countyId: null,
      }));
    } else if (name == "countryId" && value != "LR") {
      setInputData((prev) => ({
        ...prev,
        region: null,
      }));
    }
    setErrors((prev) => ({ ...prev, [name]: "" }));
  };

  useEffect(() => {
    if (initialData) setInputData(initialData);
    setHasDefaultCountry(!!countryData.find((x) => x.id === "LR"));
  }, [initialData]);

  const validate = () => {
    const newErrors: { [key: string]: string } = {};
    if (!inputData.street) newErrors.street = "Street is required";
    if (!inputData.city) newErrors.city = "City is required";
    if (Number(inputData.countryId) === 0)
      newErrors.countryId = "Country is required";

    return newErrors;
  };

  const handleSubmit = (e: React.FormEvent) => {
    e.preventDefault();
    const validationErrors = validate();
    if (Object.keys(validationErrors).length > 0) {
      setErrors(validationErrors);
      return;
    }

    onSubmit(inputData);
  };

  return (
    <form onSubmit={handleSubmit} style={{ marginTop: 16 }}>
      {/* 🔹 Responsive grid layout */}
      <Box display="grid" gridTemplateColumns="1fr" gap={2}>
        <TextField
          select
          name="countryId"
          label="Country"
          value={inputData.countryId || "LR"}
          onChange={handleChange}
          fullWidth
          margin="normal"
          error={!!errors.countryId}
          helperText={errors.countryId}
        >
          <MenuItem key={0} value="">
            Please select
          </MenuItem>

          {countryData.map((data) => (
            <MenuItem key={data.id} value={data.id} selected={data.id === "LR"}>
              {data.name}
            </MenuItem>
          ))}
        </TextField>

        {inputData.countryId == "LR" ? (
          <TextField
            select
            name="countyId"
            label="County"
            value={inputData.countyId}
            onChange={handleChange}
            fullWidth
            margin="normal"
          >
            <MenuItem key={0} value={0}>
              Please select
            </MenuItem>
            {countyData.map((data) => (
              <MenuItem key={data.id} value={data.id}>
                {data.name}
              </MenuItem>
            ))}
          </TextField>
        ) : (
          <TextField
            name="region"
            label="Region"
            value={inputData.region}
            onChange={handleChange}
            fullWidth
            margin="normal"
          />
        )}

        <TextField
          name="postalCode"
          label="Postal Code"
          value={inputData.postalCode}
          onChange={handleChange}
          fullWidth
          margin="normal"
        />

        <TextField
          name="city"
          label="City"
          value={inputData.city}
          onChange={handleChange}
          fullWidth
          margin="normal"
          error={!!errors.city}
          helperText={errors.city}
        />

        <TextField
          name="street"
          label="Street"
          value={inputData.street}
          onChange={handleChange}
          fullWidth
          margin="normal"
          error={!!errors.street}
          helperText={errors.street}
        />
      </Box>

      {/* Action buttons */}
      <Box
        display="flex"
        flexDirection={isMobile ? "column" : "row"}
        gap={2}
        marginTop={3}
      >
        <Button
          variant="outlined"
          color="secondary"
          onClick={onClose}
          fullWidth
        >
          Close
        </Button>
        <Button type="submit" variant="contained" color="success" fullWidth>
          Save
        </Button>
      </Box>
    </form>
  );
};

export default AddressForm;
